import { _GetSelectedProduct, type FindStoreResult } from 'autogen/swagger/all';
import { GeneralExceptionError } from 'autogen/translation-keys/trans-website-shared-error';
import useFindationQuickView from 'components/Findation/hooks/useFindationQuickView';
import type { IProductActionTrackingContext, ProductActionProps } from 'components/generic/productCTA/shared/types';
import useRegimeQuickView from 'components/product/regime/components/RegimeQuickView/components/RegimeQuickViewButton/hooks/useRegimeQuickView';
import { useSlides } from 'components/quickView';
import { FindInStoreProvider } from 'components/storeList/components/FindInStoreQuickView';
import { useFindInStoreQuickView } from 'components/storeList/shared/hooks';
import StoreListQuickView from 'components/storeList/storeListQuickview';
import isClient from 'helpers/isClient';
import { eventCategoryToContext } from 'modules/tracking/GA4';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'components/Toast';
import { GA4Tracking } from 'modules/tracking/GA4/GA4Tracking';

function track(trackingInfo: ProductActionProps['ClickAndCollect']['trackingInfo']) {
    if (!isClient) return;

    const { eventCategory } = trackingInfo;

    const context = eventCategoryToContext(eventCategory);

    GA4Tracking({
        eventName: 'click_and_collect',
        context,
        category: 'ecommerce'
    });
}

export function useOpenClickAndCollectCallback({
    productId,
    trackingInfo
}: {
    productId: number;
    trackingInfo: IProductActionTrackingContext;
}) {
    const [, toggle] = useFindInStoreQuickView();
    const [{ isOpen: regimeQuickViewIsOpen }] = useRegimeQuickView();
    const [{ isOpen: findationQuickViewIsOpen }] = useFindationQuickView();
    const [showClickAndCollectOnly, setShowClickAndCollectOnly] = useState<boolean>(true);
    const { push } = useSlides();
    const { t } = useTranslation();

    const onClickHandler = useCallback(async () => {
        await _GetSelectedProduct({ productId }).then((data) => {
            if (data) {
                if (regimeQuickViewIsOpen || findationQuickViewIsOpen) {
                    push(() => (
                        <FindInStoreProvider productId={productId} showClickAndCollectOnly={showClickAndCollectOnly}>
                            {(findInStoreResult: FindStoreResult | undefined) => (
                                <StoreListQuickView
                                    findStoreResult={findInStoreResult}
                                    productInfo={data}
                                    showClickAndCollectToggle={true}
                                    showClickAndCollectOnly={showClickAndCollectOnly}
                                    setShowClickAndCollectOnly={setShowClickAndCollectOnly}
                                />
                            )}
                        </FindInStoreProvider>
                    ));
                } else {
                    toggle({
                        isOpen: true,
                        productId,
                        productInfo: data,
                        showClickAndCollectToggle: true,
                        clickAndCollectOnly: showClickAndCollectOnly,
                        isClickAndCollect: true,
                        showSelectParcelShop: false
                    });
                }
            } else {
                toast.error<string>(t(GeneralExceptionError), {
                    position: 'top-right'
                });
            }
        });

        track(trackingInfo);
    }, [productId, toggle, trackingInfo, push, regimeQuickViewIsOpen, findationQuickViewIsOpen]);

    return onClickHandler;
}
